import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, MapPin, Scale, FileText, ShieldCheck, Quote } from "lucide-react";
import api from "@/lib/api";
import { useSettings, telHref } from "@/context/SettingsContext";
import { useSeo } from "@/lib/useSeo";
import { resolveImg, formatDate } from "@/lib/util";
import { DynIcon } from "@/components/site/Icon";
import { PublicLayout } from "@/components/site/PublicLayout";

const STRIP = [
  { Icon: MapPin, label: "Primary Practice", value: "Thrissur District" },
  { Icon: Scale, label: "Legal Services", value: "Representation · Consultation · Documentation" },
  { Icon: ShieldCheck, label: "Jurisdiction", value: "Thrissur & Appropriate Matters Across Kerala" },
  { Icon: Phone, label: "Contact", value: "+91 75598 97677 · +91 96457 99817" },
];

export default function Home() {
  const settings = useSettings();
  const [areas, setAreas] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/practice-areas").then((r) => setAreas(r.data.slice(0, 8)));
    api.get("/blog?limit=3").then((r) => setPosts(r.data.items));
  }, []);

  const seo = settings?.seo || {};
  const c = settings?.contact || {};
  const hero = settings?.hero || {};
  const about = settings?.about || {};
  const approach = settings?.approach || [];
  const profile = settings?.profile || {};

  useSeo({
    title: seo.home_title || "Adv. Guruvayoorappan K V | Advocate in Thrissur, Kerala",
    description: seo.home_description,
    ogImage: resolveImg(seo.default_og_image || hero.portrait_url),
    robots: "index, follow",
    jsonLd: {
      "@context": "https://schema.org", "@type": ["LegalService", "Person"],
      name: "Adv. Guruvayoorappan K V", jobTitle: "Advocate",
      address: { "@type": "PostalAddress", streetAddress: "1st Floor, KMA Towers, Kanjani Road, Chungam, West Fort", addressLocality: "Thrissur", addressRegion: "Kerala", postalCode: "680004", addressCountry: "IN" },
      telephone: [c.phone1, c.phone2], areaServed: "Thrissur, Kerala",
    },
  });

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative bg-navy overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #C5A059 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 lg:gap-6 items-center py-14 lg:py-20 relative">
          <div className="animate-fade-up">
            <p className="inline-flex items-center gap-2 text-brass font-mono text-xs tracking-[0.25em] uppercase border border-brass/30 rounded-full px-4 py-1.5 mb-6">
              <Scale size={13} /> Advocate · Thrissur, Kerala
            </p>
            <h1 className="text-ivory font-serif text-xl sm:text-2xl md:text-3xl lg:text-4xl leading-tight tracking-tight mb-3 whitespace-nowrap">
              {hero.heading || "ADV. GURUVAYOORAPPAN K V"}
            </h1>
            <p className="text-brass text-lg lg:text-xl font-serif italic mb-6">{hero.subheading || "Advocate | Thrissur, Kerala"}</p>
            <h2 className="text-ivory/90 text-xl lg:text-2xl font-serif leading-snug mb-4 text-balance">
              {hero.headline || "Professional Legal Representation & Consultation in Thrissur and Across Kerala"}
            </h2>
            <p className="text-ivory/60 text-base leading-relaxed mb-8 max-w-xl">{hero.supporting_copy}</p>
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Link to="/contact" className="bg-brass hover:bg-brass-hover text-navy font-semibold px-7 py-3.5 rounded-sm transition-colors flex items-center justify-center gap-2 tracking-wide" data-testid="hero-consultation-cta">
                Request a Consultation <ArrowRight size={17} />
              </Link>
              <Link to="/practice-areas" className="border border-ivory/25 hover:border-brass text-ivory hover:text-brass font-semibold px-7 py-3.5 rounded-sm transition-colors flex items-center justify-center gap-2 tracking-wide" data-testid="hero-practice-cta">
                Explore Practice Areas
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-ivory/70 text-sm">
              <span className="font-mono text-xs uppercase tracking-widest text-ivory/40">Call Now</span>
              <a href={telHref(c.phone1)} className="flex items-center gap-2 hover:text-brass transition-colors" data-testid="hero-phone1"><Phone size={15} className="text-brass" />{c.phone1 || "+91 75598 97677"}</a>
              <a href={telHref(c.phone2)} className="flex items-center gap-2 hover:text-brass transition-colors" data-testid="hero-phone2"><Phone size={15} className="text-brass" />{c.phone2 || "+91 96457 99817"}</a>
            </div>
          </div>

          <div className="relative animate-fade-in lg:justify-self-end">
            <div className="absolute -inset-4 bg-gradient-to-tr from-brass/25 to-transparent rounded-[2rem] blur-2xl" />
            <div className="relative rounded-t-[10rem] rounded-b-lg overflow-hidden border border-brass/30 bg-navy-light max-w-sm mx-auto lg:mx-0" style={{ aspectRatio: "3/4" }}>
              {hero.portrait_url ? (
                <img src={resolveImg(hero.portrait_url)} alt="Adv. Guruvayoorappan K V, Advocate, Thrissur" className="w-full h-full object-cover object-top" data-testid="hero-portrait" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-ivory/30"><Scale size={64} /></div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* QUICK INFO STRIP */}
      <section className="bg-navy-light border-t border-brass/15" data-testid="info-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-ivory/10">
          {STRIP.map((s, i) => (
            <div key={i} className="p-6 lg:p-8">
              <s.Icon size={20} className="text-brass mb-3" />
              <p className="text-ivory/40 text-[11px] font-mono tracking-widest uppercase mb-1">{s.label}</p>
              <p className="text-ivory text-sm font-medium leading-snug">{s.value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center" data-testid="about-preview">
        <div className="relative order-2 lg:order-1">
          <div className="relative rounded-lg overflow-hidden bg-navy border border-brass/20 shadow-xl flex items-end justify-center" style={{ aspectRatio: "4/5" }}>
            <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #C5A059 1px, transparent 0)", backgroundSize: "24px 24px" }} />
            {about.image_url ? <img src={resolveImg(about.image_url)} alt="Adv. Guruvayoorappan K V" className="relative z-10 w-auto h-[95%] object-contain" /> : <div className="w-full h-full bg-navy/5" />}
          </div>
          <div className="hidden lg:block absolute -bottom-6 -right-6 bg-brass text-navy p-5 rounded-lg max-w-[200px] shadow-lg">
            <p className="font-serif text-sm leading-snug">Practicing as a Counsel since 2017.</p>
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <p className="text-brass font-mono text-xs tracking-[0.25em] uppercase mb-4">About</p>
          <h2 className="font-serif text-3xl lg:text-4xl text-navy mb-6 leading-tight">{about.title || "ABOUT ADV. GURUVAYOORAPPAN K V"}</h2>
          <p className="text-slate-600 leading-relaxed text-base lg:text-lg mb-6">{about.text}</p>
          {profile.qualifications?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5" data-testid="about-credentials">
              {profile.qualifications.map((q, i) => (
                <span key={i} className="text-xs font-medium bg-ivory-dark border border-slate-200 text-navy px-3 py-1.5 rounded-full">{[q.qualification, q.field].filter(Boolean).join(" ")}</span>
              ))}
            </div>
          )}
          <div className="border-l-2 border-brass pl-4 mb-8">
            <p className="text-navy font-medium text-sm">Practicing as a Counsel since 2017</p>
            <p className="text-slate-500 text-sm">Former Central Government Additional Standing Counsel — 3 Years</p>
          </div>
          <Link to="/about" className="inline-flex items-center gap-2 text-navy font-semibold border-b-2 border-brass pb-1 hover:gap-3 transition-all" data-testid="about-read-more">
            View Full Profile <ArrowRight size={17} />
          </Link>
        </div>
      </section>

      {/* PRACTICE AREAS */}
      <section className="bg-ivory-dark py-16 lg:py-24" data-testid="practice-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-12">
            <p className="text-brass font-mono text-xs tracking-[0.25em] uppercase mb-4">Areas of Practice</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-navy leading-tight">Legal matters handled with diligence and care</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {areas.map((a) => (
              <Link key={a.id} to={`/practice-areas/${a.slug}`} className="group bg-white border border-slate-200 rounded-lg p-6 hover:border-brass hover:shadow-lg transition-all" data-testid={`practice-card-${a.slug}`}>
                <span className="w-11 h-11 rounded-sm bg-navy text-brass flex items-center justify-center mb-4 group-hover:bg-brass group-hover:text-navy transition-colors">
                  <DynIcon name={a.icon} size={20} />
                </span>
                <h3 className="font-serif text-lg text-navy mb-2 leading-snug">{a.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed line-clamp-3">{a.short_desc}</p>
                <span className="inline-flex items-center gap-1 text-brass text-sm font-medium mt-4 group-hover:gap-2 transition-all">Learn More <ArrowRight size={14} /></span>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/practice-areas" className="inline-flex items-center gap-2 border border-navy/20 hover:border-brass text-navy font-semibold px-7 py-3 rounded-sm transition-colors" data-testid="practice-view-all">
              View All Practice Areas <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* PROFESSIONAL APPROACH */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24" data-testid="approach-section">
        <div className="max-w-2xl mb-12">
          <p className="text-brass font-mono text-xs tracking-[0.25em] uppercase mb-4">Professional Approach</p>
          <h2 className="font-serif text-3xl lg:text-4xl text-navy leading-tight">A considered, client-focused way of working</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {approach.map((p, i) => (
            <div key={i} className="border-t-2 border-brass pt-5" data-testid={`approach-${i}`}>
              <span className="font-serif text-3xl text-brass/40">0{i + 1}</span>
              <h3 className="font-serif text-xl text-navy mt-2 mb-2">{p.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* THOUGHT ON LAW */}
      {settings?.profile?.thought && (
        <section className="bg-navy py-16 lg:py-24" data-testid="thought-section">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Quote size={40} className="text-brass mx-auto mb-6" />
            <p className="text-brass font-mono text-xs tracking-[0.25em] uppercase mb-6">Thought on Law & Society</p>
            <blockquote className="text-ivory font-serif text-2xl lg:text-3xl leading-relaxed italic">"{settings.profile.thought}"</blockquote>
          </div>
        </section>
      )}

      {/* LATEST ARTICLES */}
      {posts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24" data-testid="latest-articles">
          <div className="flex justify-between items-end mb-12 flex-wrap gap-4">
            <div>
              <p className="text-brass font-mono text-xs tracking-[0.25em] uppercase mb-4">Law & Society</p>
              <h2 className="font-serif text-3xl lg:text-4xl text-navy leading-tight">Latest legal insights</h2>
            </div>
            <Link to="/blog" className="text-navy font-semibold border-b-2 border-brass pb-1 hover:gap-2 inline-flex items-center gap-1 transition-all">View All <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {posts.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="group" data-testid={`home-post-${p.slug}`}>
                <div className="rounded-lg overflow-hidden mb-4 border border-slate-200" style={{ aspectRatio: "3/2" }}>
                  {p.featured_image ? <img src={resolveImg(p.featured_image)} alt={p.featured_image_alt || p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full bg-navy/5" />}
                </div>
                <span className="text-brass text-xs font-mono uppercase tracking-widest">{p.category}</span>
                <h3 className="font-serif text-lg text-navy mt-2 mb-2 leading-snug group-hover:text-brass transition-colors">{p.title}</h3>
                <p className="text-slate-400 text-xs">{formatDate(p.published_at)}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </PublicLayout>
  );
}
